import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Box, Typography, Button, Dialog, DialogTitle, 
         DialogContent, DialogActions, Alert, Divider,Paper, Grid } from '@mui/material';
import toast from 'react-hot-toast';

import Breadcrumb from 'src/ui-component/Breadcrumb';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import LinearStepper from 'src/ui-component/Stepper';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import PdfGenerator from 'src/ui-component/PdfGenerator';

import EstablishmentInfo from './EstablishmentInfo';
import EstablishmentType, { EstablishmentFormData, SpecialEventData, EstablishmentTypeData } from './EstablishmentType';
import OwnerInfo from './OwnerInfo';
import AdditionalInfo from './AdditionalInfo';
import DocumentUpload from './DocumentUpload';

//import { FieldsSchema } from './Schema';
import { RootState } from 'src/redux-store/appStore';
import { resetFormData, updateFormData, updateAppId } from 'src/redux-store/slices/foodAndBeverageFormSlice';
import { httpRequest } from 'src/services/httpRequest';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { FieldsSchema } from './Schema';

// Default establishment form data aligned with EstablishmentType component
const DEFAULT_ESTABLISHMENT_FORM_DATA: EstablishmentFormData = {
  'Restaurant': {
    'Restaurant': false,
    id: null,
    size: '',
    fee: null
  },
  'RetailFoodEstablishment': {
    'RetailFoodEstablishment': false,
    id: null,
    size: '',
    fee: null
  },
  'MobileVehicle': {
    'MobileVehicle': false,
    id: null,
    size: '',
    fee: null
  },
  'Special Event': {
    status: false,
    eventParticipationType: '',
    eventAddress: '',
    eventDateFrom: '',
    eventDateTo: '',
    rainDateFrom: '',
    rainDateTo: ''
  }
};

const NewApplication = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { townshipId, permit } = useParams();
  
  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [applicationId, setApplicationId] = useState(0);
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [taxRate, setTaxRate] = useState(0);
  const [showPendingChecksDialog, setShowPendingChecksDialog] = useState(false);
  const [pendingChecks, setPendingChecks] = useState([]);
  
  // Redux state

  const userData = useSelector((store : RootState) => store.user.userDetails);
  const formData = useSelector((state : RootState) => state.foodBeverageFormSlice.formData);
  const appId = useSelector((state : RootState) => state.foodBeverageFormSlice.AppId.applicationId);

  // Form references for validation
  const formRefs = {
    establishmentInfo: useRef(null),
    establishmentType: useRef(null),
    ownerInfo: useRef(null),
    additionalInfo: useRef(null),
    documentUpload: useRef(null)
  };

  // Initialize establishmentType data structure if it doesn't exist
  useEffect(() => {
    if (!formData.EstablishmentType) {
      dispatch(updateFormData({ EstablishmentType: DEFAULT_ESTABLISHMENT_FORM_DATA }));
    }
  }, [dispatch, formData.EstablishmentType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetFormData());
    };
  }, [dispatch]);

  // Method to get application fee
  const handleGetApplicationFee = async () => {
    setLoading(true);
    try {
      const response = await httpRequest('GET', `/configuration/fee_details?PermitID=${permit}`);
      if (response?.status === 'success') {
        const fees = response.data;
        const appFee = fees.find(f => f.FeeTypeName === 'Application Fee');
        const inspectFee = fees.find(f => f.FeeTypeName === 'Inspection Fee');
        const creditFee = fees.find(f => f.FeeTypeName === 'Credit Card Fee');

        setFeeDetails({
          applicationFee: parseFloat(appFee?.FeeValue || '0'),
          inspectionFee: parseFloat(inspectFee?.FeeValue || '0')
        });
        setTaxRate(parseFloat(creditFee?.FeeValue || '0'));
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
    } catch (error) {
      dispatch(handleError("Failed to fetch application fees"));
    } finally {
      setLoading(false);
    }
  };

  // Get application fees
  useEffect(() => {
    if (permit) {
      handleGetApplicationFee();
    }
  }, [permit]);

  const FeeSummarySection = () => {
    const totalFee = feeDetails.applicationFee + feeDetails.inspectionFee;
  
    return (
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md={6}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: '#f9fafb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            {/* Fees */}
            {[ 
              { label: 'Application Fee', value: `$${feeDetails.applicationFee.toFixed(2)}` },
              { label: 'Inspection Fee', value: `$${feeDetails.inspectionFee.toFixed(2)}` },
              ...(taxRate > 0
                ? [{ label: 'Credit Card Processing Fee', value: `${taxRate}%` }]
                : [])
            ].map((item, index) => (
              <React.Fragment key={item.label}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography>{item.label}</Typography>
                  <Typography>{item.value}</Typography>
                </Box>
                {index < 2 && <Divider />}
              </React.Fragment>
            ))}
  
            <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
  
            {/* Total */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight="bold">
                Total Fee
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                ${totalFee.toFixed(2)}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const getCurrentFormRef = () => {
    switch (activeStep) {
      case 0: return formRefs.establishmentInfo;
      case 1: return formRefs.establishmentType;
      case 2: return formRefs.ownerInfo;
      case 3: return formRefs.additionalInfo;
      case 4: return formRefs.documentUpload;
      default: return null;
    }
  };

  const handleStepSubmit = async () => {
    const currentFormRef = getCurrentFormRef();
    if (currentFormRef?.current) {
      try {
        await currentFormRef.current.submitForm();
        const errors = await currentFormRef.current.validateForm();
        return Object.keys(errors).length === 0;
      } catch (error) {
        return false;
      }
    }
    return true; // If no form ref (e.g., Summary step), allow proceeding
  };

  // Handle establishment type changes
  const handleEstablishmentTypeChange = (type, checked) => {
    const updatedEstablishmentType = { ...formData.EstablishmentType };
    
    if (type === 'Special Event') {
      // For Special Event, update the status property
      updatedEstablishmentType[type] = {
        ...updatedEstablishmentType[type],
        status: checked
      };
    } else {
      // For regular establishment types
      updatedEstablishmentType[type] = {
        ...updatedEstablishmentType[type],
        [type]: checked
      };
      
      // Reset the size selection if unchecked
      if (!checked) {
        updatedEstablishmentType[type].id = null;
        updatedEstablishmentType[type].size = '';
        updatedEstablishmentType[type].fee = null;
      }
    }
    
    dispatch(updateFormData({ EstablishmentType: updatedEstablishmentType }));
  };

  // Handle size selection
  const handleSizeSelection = (type, sizeId, sizeName, fee) => {
    const updatedEstablishmentType = { ...formData.EstablishmentType };
    
    updatedEstablishmentType[type] = {
      ...updatedEstablishmentType[type],
      id: sizeId,
      size: sizeName,
      fee: fee
    };
    
    dispatch(updateFormData({ EstablishmentType: updatedEstablishmentType }));
  };

  // Handle special event field changes
  const handleSpecialEventChange = (fieldName, value) => {
    const updatedEstablishmentType = { ...formData.EstablishmentType };
    
    updatedEstablishmentType['Special Event'] = {
      ...updatedEstablishmentType['Special Event'],
      [fieldName]: value
    };
    
    dispatch(updateFormData({ EstablishmentType: updatedEstablishmentType }));
  };

  // Handle cancel
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel the application? All unsaved data will be lost.')) {
      dispatch(resetFormData());
      navigate(`/dashboard/${permit}`);
    }
  };

  // Prepare payload for API submission
  const preparePayload = () => {

    // Get establishment type data
    const establishmentTypeData = formData.EstablishmentType || DEFAULT_ESTABLISHMENT_FORM_DATA;

    // Build establishment type payload
    const buildEstablishmentTypePayload = () => {
      const result = {};
      
      // Process regular establishment types
      Object.entries(establishmentTypeData).forEach(([type, data]) => {
        if (type === 'Special Event') {
          // Special event has a different structure
          const specialEventData = data as SpecialEventData;
          result[type] = {
            Status: specialEventData.status,
            EventParticipationType: specialEventData.eventParticipationType || '',
            EventAddress: specialEventData.eventAddress || '',
            EventDateFrom: specialEventData.eventDateFrom || '',
            EventDateTo: specialEventData.eventDateTo || '',
            RainDateFrom: specialEventData.rainDateFrom || '',
            RainDateTo: specialEventData.rainDateTo || ''
          };
        } else {
          // Regular establishment types
          const typeData = data as EstablishmentTypeData;
          const apiKeyName = type; // Same as the type name
          
          result[apiKeyName] = {
            [apiKeyName]: !!typeData[type],
            id: typeData.id || null,
            size: typeData.size || null,
            status: false
          };
        }
      });
      
      return result;
    };

    return {
      // Establishment Information
      EstablishmentName: formData.establishmentName || '',
      EstablishmentEmail: formData.establishmentEmail || '',
      EstablishmentPhone: formData.establishmentPhone || '',
      EstablishmentAddress: formData.establishmentAddress || '',
      EstablishmentTown: formData.establishmentTown || '',
      BlockLot: formData.blockLot || '',
      EstablishmentContactName: formData.establishmentContactName || '',
      
      // Owner Information
      ApplicantName: formData.applicantName || '',
      ApplicantAddress: formData.applicantAddress || '',
      ApplicantSuite: formData.applicantSuitePOBox || '',
      ApplicantCity: formData.applicantCity || '',
      ApplicantState: formData.applicantState || '',
      ApplicantZipCode: formData.applicantZipCode || '',
      ApplicantEmail: formData.applicantEmail || '',
      ApplicantPhone: formData.applicantPhone || '',
      
      // Establishment Type
      EstablishmentType: buildEstablishmentTypePayload(),
      
      // Additional Information
      HasSecondaryContact: formData.hasSecondaryContactInfo || false,
      SecondaryContactName: formData.secondaryContactName || '',
      SecondaryDepartment: formData.secondaryDepartment || '',
      SecondaryAddress: formData.secondaryAddress || '',
      SecondaryEmail: formData.secondaryEmail || '',
      SecondaryPhone: formData.secondaryPhone || '',
      
      // Application Details
      ApplicationType: 'New',
      ApplicationTitle: `Food and Beverage License - ${formData.establishmentName || ''}`,
      ApplicationStatus: 'Draft',
      ApplicationFee: feeDetails.applicationFee,
      InspectionFee: feeDetails.inspectionFee,
      AdditionalEscrowFee: 0.00,
      
      //Document Upload
      DocumentFileUpload: formData.DocumentFileUpload ? formData.DocumentFileUpload.map(doc => ({
        Type: doc.Type || 'document',
        FileName: doc.FileName || ''
      })) : []
      
    };
  };

  // Handle insert application (create new)
  const handleInsertApplication = async (data, type = '') => {
    setLoading(true);
    try {
      const response = await httpRequest('POST', `/food_beverage/insert`, data);
      if (response?.status === 'success') {
        toast.success('Application created successfully.');
        const newAppId = response?.data?.ApplicationID;
        setApplicationId(newAppId);
        dispatch(updateAppId({ applicationId: newAppId}));

        // If type is provided, just return without redirecting
        if (type) return newAppId;
          
        // Add redirect to draft application page after successful creation
        if (data.ApplicationStatus === 'Draft') {
          navigate(`/${townshipId}/Department of Health and Mental Hygiene/${permit}/draft-application`);
        }

        return newAppId;
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
        return false;
      }
    } catch (error) {
      dispatch(handleError("Failed to create application"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Handle update application
  const handleUpdateApplication = async (data, type = '') => {
    setLoading(true);
    try {
      //const appIdToUse = appId;
      const response = await httpRequest(
        'PUT',
        `/food_beverage/update/${appId}`,
        data
      );
      
      if (response?.status === 'success') {
        if (type) return true;
        toast.success('Application updated successfully.');
        navigate(`/${townshipId}/Department of Health and Mental Hygiene/${permit}/draft-application`);
        return true;
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
        return false;
      }
    } catch (error) {
      dispatch(handleError("Failed to update application"));
      return false;
    } finally {
      setLoading(false);
    }
  };

   // Function to navigate to payment page
   const proceedToPaymentPage = (currentAppId) => {
    const paymentSummary = {
      fees: Object.fromEntries(
        Object.entries(feeDetails).filter(([, value]) => value > 0)
      ),
      ...(taxRate > 0 ? { taxRate } : {})
    };
    
    const paymentApplicationDetails = {
      applicationDetails: {
        userName: `${userData?.FirstName} ${userData?.LastName}`,
        applicationNumber: null,
        applicationId: currentAppId,
        applicationStatus: 'Draft',
        statusPayload: 'In Review',
        permitPrefix: 'FB',
        onPaymentCancel: `/${townshipId}/Department of Health and Mental Hygiene/${permit}/draft-application`
      }
    };
    
    navigate(`/payment-methods`, {
      state: { paymentSummary, paymentApplicationDetails }
    });
  };

  // Handle proceed to payment
  const handleProceedToPayment = async () => {
   let currentAppId = appId || applicationId;
    
    // If no application ID exists yet, create a new application
    if (!currentAppId) {
      const payload = preparePayload();
      payload.ApplicationStatus = 'Draft';
       currentAppId = await handleInsertApplication(preparePayload(), 'createForPayment');
      if (!currentAppId) return;
    } else
    {
    const payload = preparePayload();
    payload.ApplicationStatus = 'Draft';
    const isUpdated = await handleUpdateApplication(payload, 'updateForPayment');
    if (!isUpdated) return;
    }
    
    // Now we have an application ID, let's check for any pending requirements
    setIsChecking(true);
    
    try {
      // This is a placeholder for the actual check. In your implementation,
      // you might need an API endpoint similar to the Construction app
      const checkResponse = await httpRequest(
        'GET',
        `/food_beverage/${appId}/check_requirements_status`
      );
      
      if (checkResponse?.status === 'success') {
        // No pending requirements or checks needed
        if (checkResponse.message === 'No pending requirements' || 
            (Array.isArray(checkResponse.data) && checkResponse.data.length === 0)) {
          // Proceed with payment
          proceedToPaymentPage(currentAppId);
        } else {
          // Has pending requirements
          setPendingChecks(checkResponse.data);
          setShowPendingChecksDialog(true);
        }
      } else {
        // Assume no blocking issues if the check endpoint isn't implemented yet
        // You can remove this assumption when the endpoint is available
        proceedToPaymentPage(currentAppId);
      }
    } catch (error) {
      toast.error("Failed to check application requirements");
      dispatch(handleError("Failed to check application requirements"));
      proceedToPaymentPage(currentAppId);
    } finally {
      setIsChecking(false);
    }
  };

  // Handle save draft
  const handleSaveDraft = async () => {
    const isValid = await handleStepSubmit();
    if (isValid) {
      const payload = preparePayload();
      payload.ApplicationStatus = 'Draft';
      
      // If we already have an application ID, update it
      if (appId) {
        handleUpdateApplication(payload);
      } else {
        // Otherwise create a new draft
        handleInsertApplication(payload);
      }
    }
  };
  // This function will be passed to the DocumentUpload component
  const handleDocumentSubmit = (values) => {
    if (values?.DocumentFileUpload) {
      dispatch(updateFormData({ DocumentFileUpload: values.documents }));
    }
  };

  // Unified button action handler - similar to Construction app
  const handleButtonAction = async (type) => {
    switch (type) {
      case 'Next': {
        const isValid = await handleStepSubmit();
        if (isValid) {
          setActiveStep((prev) => prev + 1);
        }
        break;
      }
      case 'Back':
        setActiveStep((prev) => prev - 1);
        break;
      case 'Proceed to Payment':
        handleProceedToPayment();
        break;
      case 'Update':
      case 'Save Draft':
        handleSaveDraft();
        break;
      case 'Cancel':
        handleCancel();
        break;
      default:
        break;
    }
  };

  const steps = [
    {
      label: 'Establishment Information',
      Component: (
        <EstablishmentInfo
          ref={formRefs.establishmentInfo}
          formData={formData}
          setFieldValue={(field, value) => dispatch(updateFormData({ [field]: value }))}
          handleSubmit={() => {
            dispatch(updateFormData({ DocumentFileUpload: [] }));
          }}
        />
      )
    },
     {
      label: 'Establishment Type',
      Component: (
        <EstablishmentType
           ref={formRefs.establishmentType}
           establishmentData={formData.EstablishmentType || DEFAULT_ESTABLISHMENT_FORM_DATA}
           handleEstablishmentTypeChange={handleEstablishmentTypeChange}
           handleSizeSelection={handleSizeSelection}
           handleSpecialEventChange={handleSpecialEventChange}
           handleSubmit={() => dispatch(updateFormData({ DocumentFileUpload: [] }))}
           errors={{}}
           touched={{}}
        />
      )
    },
    {
      label: 'Owner Information',
      Component: (
        <OwnerInfo
          ref={formRefs.ownerInfo}
          formData={formData}
          setFieldValue={(field, value) => dispatch(updateFormData({ [field]: value }))}
          handleSubmit={() => {
            dispatch(updateFormData({ DocumentFileUpload: [] }));
          }}
        />
      )
    },
    {
      label: 'Additional Information',
      Component: (
        <AdditionalInfo
          ref={formRefs.additionalInfo}
          formData={formData}
          setFieldValue={(field, value) => dispatch(updateFormData({ [field]: value }))}
          handleSubmit={() => {
            dispatch(updateFormData({ DocumentFileUpload: [] }));
          }}
        />
      )
    },
    {
      label: 'Upload Documents',
      Component: (
        <DocumentUpload
        ref={formRefs.documentUpload}
        formData={{ DocumentFileUpload: formData?.DocumentFileUpload || [] }}
        ApplicationId={applicationId || appId}
        permitName="Food and Beverage"
        isButtonHide={true}
        handleSubmit={handleDocumentSubmit}
      />
      )
    },
    {
      label: 'Summary',
      Component: (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: '1200px', mx: 'auto' }}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, md: 4 },
              mb: 4,
              borderRadius: 3,
              backgroundColor: '#ffffff',
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                textAlign: 'center',
                mb: 3,
              }}
            >
              Application Summary
            </Typography>
    
            <Alert
              severity="info"
              icon={false}
              sx={{
                fontWeight: 600,
                backgroundColor: '#e8f0fe',
                color: '#1f2937',
                textAlign: 'center',
                mb: 4,
                py: 2,
                justifyContent:'center'
              }}
            >
              Please review your application details before proceeding.
            </Alert>
    
            <FeeSummarySection />
          </Paper>
        </Box>
      )
    }
  ];
  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2, pb: 4, px: {xs: 1, md: "auto"} }} className="main-table-container">
      <Breadcrumb PermitName="Food and Beverage" PageTitle="New Application" />
      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType="new"
      />

      <Card>
        <Box m={{ md: 3, xs: 1 }} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">New Application</Typography>
          <PdfGenerator 
           PermitName="Food and Beverage" 
           allFields={formData ? FieldsSchema(formData) : []} 
          />
        </Box>

        <Box m={{ md: 3, xs: 1 }}>
          <LinearStepper
            activeStep={activeStep}
            steps={steps}
            stepperButton={[
              { 
                label: 'Back', 
                function: () => handleButtonAction('Back') 
              },
              { 
                label: 'Next', 
                function: () => handleButtonAction('Next') 
              }
            ]}
            finalStepButtons={[
              {
                label: 'Cancel',
                function: () => handleButtonAction('Cancel'),
                // color: 'error',
                // variant: 'outlined'
              },
              {
                label: 'Save Draft',
                function: () => handleButtonAction('Save Draft'),
                // color: 'primary',
                // variant: 'outlined'
              },
              {
                label: isChecking ? 'Checking Status...' : 'Proceed to Payment',
                function: () => handleButtonAction('Proceed to Payment'),
                // color: 'primary',
                // variant: 'contained',
                disabled: isChecking
              }
            ]}
          />
          
          {/* Dialog for pending checks/requirements */}
          <Dialog
            open={showPendingChecksDialog}
            onClose={() => setShowPendingChecksDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Typography variant="h4" color="error">
                Cannot Proceed to Payment
              </Typography>
            </DialogTitle>
            <Divider />
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  You must fulfill all requirements before proceeding to payment.
                </Alert>
                <br />
                <Typography variant="body1">
                  You have pending requirements that need to be completed:
                </Typography>
              </Box>

              {pendingChecks.map((check, index) => (
                <Box
                  key={index}
                  sx={{ mb: 3, p: 2, bgcolor: '#fff4e5', borderRadius: 1 }}
                >
                  <Typography fontWeight="bold">
                    {check.name} - {check.status}
                  </Typography>
                  <Typography variant="body2">{check.description}</Typography>
                </Box>
              ))}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setShowPendingChecksDialog(false)}
                variant="contained"
              >
                OK, I'll Complete These First
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Card>
    </Container>
  );
};

export default NewApplication;